/**
 * Objective palette. Used in two places (and likely more later): the canvas
 * node card renders objective dots by tag; the ObjectiveTagsEditor renders
 * a chip per declared objective in the editor.
 *
 * Colors are assigned in **declaration order** — i.e., the index of the
 * objective id within `Object.keys(scenario.objectives)`. Deleting an earlier
 * objective shifts the remaining ones (documented in the skill). If you ever
 * want stable-on-delete colors, switch to a hash function on the id; until
 * authors complain, declaration order is simpler and predictable.
 *
 * The 8-entry palette cycles for scenarios with more than 8 objectives.
 * Real scenarios have a handful, not dozens — the cycle is defensive, not a
 * limit anyone should hit.
 */
export const OBJECTIVE_PALETTE = [
  '#B8551F', '#6B8CAE', '#6B8C5C', '#8C6BAE',
  '#AE8C6B', '#6BAE8C', '#AE6B6B', '#6B6BAE',
] as const;

/** Fallback color when an objectiveTags entry points at a missing objective. */
export const OBJECTIVE_MISSING_COLOR = '#999388';

/**
 * Look up the palette color for an objective id. Pass the whole `objectives`
 * map — the function needs declaration order, which it gets from
 * `Object.keys`. Returns the missing-color sentinel for ids that aren't
 * declared (the validator will surface those as `UNDECLARED_OBJECTIVE`; the
 * UI just needs to render gracefully).
 */
export function objectiveColor(
  objectives: Record<string, unknown>,
  id: string,
): string {
  const ids = Object.keys(objectives || {});
  const idx = ids.indexOf(id);
  if (idx < 0) return OBJECTIVE_MISSING_COLOR;
  return OBJECTIVE_PALETTE[idx % OBJECTIVE_PALETTE.length];
}
